import './checkbox.scss';

import { MouseEvent, ReactNode, useId } from 'react';

import { CheckedIcon } from '../Icons/CheckedIcon';
import { UncheckedIcon } from '../Icons/UncheckedIcon';
import { toErrorMessage } from '../Input/functions/toErrorMessage';
import { CheckboxProps } from './types/CheckboxProps';

export const Checkbox = ({
  className,
  description,
  error = false,
  errorTestId,
  label,
  onClick,
  testId,
  variant = 'default',
  ...props
}: CheckboxProps): ReactNode => {
  const labelId = useId();

  const checked = Boolean(props.checked);
  const hasError = Boolean(error);
  const errorMessage = toErrorMessage(error);

  const handleClick = (event: MouseEvent<HTMLInputElement>): void => {
    // WebKit never focuses a tapped checkbox, so it would never blur and an
    // `onTouched` form would never validate it.
    event.currentTarget.focus();

    if (onClick) {
      onClick(event);
    }
  };

  const cardClassNames = [
    'checkbox-described',
    variant === 'sm' && 'checkbox-described-sm',
    checked && 'checkbox-described-checked',
    hasError && 'checkbox-described-error',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <label className={cardClassNames}>
        <span className="checkbox-described-header">
          <span className="checkbox-control" data-testid={testId}>
            <input
              aria-invalid={hasError}
              aria-labelledby={label ? labelId : undefined}
              {...props}
              className="checkbox-input"
              type="checkbox"
              onClick={handleClick}
            />
            {checked ? <CheckedIcon /> : <UncheckedIcon />}
          </span>

          {label && (
            <span className="checkbox-described-label" id={labelId}>
              {label}
            </span>
          )}
        </span>

        {description && (
          <span className="checkbox-described-description">{description}</span>
        )}
      </label>

      {errorMessage && (
        <span className="field-error" data-testid={errorTestId}>
          {errorMessage}
        </span>
      )}
    </>
  );
};
